
import { useMemo } from 'react';
import { 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  format, 
  getDay, 
  isSameDay, 
  parseISO
} from 'date-fns';
import { Class, ClassSchedule } from '@/types/classTypes';

export const useCalendarEvents = (
  schedules: ClassSchedule[],
  classes: Class[],
  selectedDate: Date,
  selectedMonth: Date,
  filterType: string
) => {
  // Filter classes based on the filter type
  const filteredClasses = useMemo(() => {
    if (filterType === "all") {
      return classes;
    } else if (filterType === "events") {
      return classes.filter(cls => cls.is_event);
    } else if (filterType === "classes") {
      return classes.filter(cls => !cls.is_event);
    } else {
      // Filter by specific class
      return classes.filter(cls => cls.id === filterType);
    }
  }, [classes, filterType]);

  // Get days in the selected month
  const daysInMonth = useMemo(() => {
    return eachDayOfInterval({
      start: startOfMonth(selectedMonth),
      end: endOfMonth(selectedMonth)
    });
  }, [selectedMonth]);

  // Group events by date for easy lookup
  const eventsByDate = useMemo(() => {
    const events: Record<string, { classId: string; classData: Class }[]> = {};

    // Process regular class schedules
    schedules.forEach(schedule => {
      // Use normalized weekday value from schedule
      const weekdayValue = schedule.weekday;
      
      if (weekdayValue === undefined) {
        return;
      }
      
      const classData = classes.find(cls => cls.id === schedule.class_id);
      if (!classData) {
        return;
      }
      
      // Skip if the class is not in the filtered classes
      if (!filteredClasses.some(fc => fc.id === classData.id)) {
        return;
      }

      // For regular weekly classes, calculate all occurrences in the month
      daysInMonth.forEach(day => {
        if (getDay(day) === weekdayValue) {
          const dateKey = format(day, 'yyyy-MM-dd');
          if (!events[dateKey]) events[dateKey] = [];
          events[dateKey].push({ classId: schedule.class_id, classData });
        }
      });
    });

    // Process one-time events
    filteredClasses.forEach(cls => {
      if (cls.is_event && cls.event_date) {
        try {
          const eventDate = parseISO(cls.event_date);
          const dateKey = format(eventDate, 'yyyy-MM-dd');
          
          if (isSameDay(eventDate, selectedDate) || 
              (eventDate >= startOfMonth(selectedMonth) && 
               eventDate <= endOfMonth(selectedMonth))) {
            if (!events[dateKey]) events[dateKey] = [];
            events[dateKey].push({ classId: cls.id, classData: cls });
          }
        } catch (error) {
          console.error("Error processing event date for class:", cls.name, error);
        }
      }
    });
    
    return events;
  }, [daysInMonth, schedules, classes, filteredClasses, selectedDate, selectedMonth]);

  return { filteredClasses, daysInMonth, eventsByDate };
};
